import httpDao from "../infrastructure/httpDao";
import {
  getCssCorrectionReasonError,
  normalizeCssCorrectionReason,
} from "@/lib/css-enrollment-correction";

export type DirectDebitProvider =
  | "none"
  | "mock"
  | "file_transfer"
  | "css_net"
  | "paygent"
  | "robot_payment";

export type DirectDebitConfig = {
  enabled: boolean;
  provider: DirectDebitProvider;
};

export type MemberDirectDebitRegistration = {
  status:
    | "NOT_REGISTERED"
    | "IN_PROGRESS"
    | "SUBMITTED"
    | "CORRECTION_REQUIRED"
    | "REGISTERED";
  submittedAt: string | null;
  registeredAt: string | null;
  correctionRequest: { reason: string; requestedAt: string } | null;
};

export type DirectDebitRegistrationSettings = DirectDebitConfig & {
  mode: "production" | "test";
  registrationFlow: "css_fillin" | "manual" | "mock";
  available: boolean;
  registrationUrl: string | null;
  qrCodeValue: string | null;
};

export type DirectDebitSchedule = {
  id: string;
  provider: DirectDebitProvider;
  timezone: "Asia/Tokyo";
  transferDate: string;
  accountRegistrationDeadline: string | null;
  requestSubmissionStartAt: string | null;
  internalFinalizationDeadlineAt: string;
  requestDeadlineAt: string;
  resultAvailableDate: string | null;
  settlementExpectedDate: string | null;
  sourceReference: string | null;
  createdAt: string;
  updatedAt: string;
};

export type DirectDebitScheduleList = {
  year: number;
  timezone: "Asia/Tokyo";
  items: DirectDebitSchedule[];
};

export type UpsertDirectDebitScheduleInput = {
  scheduleId?: string;
  transferDate: string;
  accountRegistrationDeadline?: string;
  requestSubmissionStartAt?: string;
  internalFinalizationDeadlineAt: string;
  requestDeadlineAt: string;
  resultAvailableDate?: string;
  settlementExpectedDate?: string;
  sourceReference?: string;
};

export type RegisterMockBankAccountInput = {
  customerId: string;
  bankCode: string;
  bankName: string;
  branchCode: string;
  branchName: string;
  accountType: "1" | "2";
  accountNumber: string;
  accountHolder: string;
};

export type PublicMockBankAccountInput = Omit<
  RegisterMockBankAccountInput,
  "customerId"
> & { subscriberCode?: string };

export type MockRegistrationSession = {
  status: "READY" | "COMPLETED";
  mode: "test";
  expiresAt: string;
  requiresSubscriberCode?: boolean;
};

export type CssEnrollmentCandidate = {
  enrollmentId: string | null;
  parentId: string;
  parentName: string;
  email: string | null;
  phoneNumber: string | null;
  status: "NOT_STARTED" | "EXPORTED" | "REGISTERED";
  subscriberCode: string | null;
  canExport: boolean;
  available: boolean;
  applicationStatus:
    | "NOT_STARTED"
    | "IN_PROGRESS"
    | "SUBMITTED"
    | "CORRECTION_REQUIRED";
  applicationRevision: number;
  applicationSubmittedAt: string | null;
  correctionRequest: {
    reason: string;
    requestedAt: string;
    requestedByName: string | null;
  } | null;
};

export type CssCorrectionRequestResult = Pick<
  CssEnrollmentCandidate,
  | "enrollmentId"
  | "parentId"
  | "status"
  | "subscriberCode"
  | "canExport"
  | "available"
  | "applicationStatus"
  | "applicationRevision"
  | "applicationSubmittedAt"
  | "correctionRequest"
>;

export type DirectDebitCandidate = {
  tuitionInvoiceId: string;
  bankAccountId: string | null;
  clientReference: string | null;
  parentId: string;
  parentName: string;
  facilityName: string | null;
  billingPeriod: string;
  collectionPeriod: string;
  amount: number;
  currency: string;
  bankCode: string | null;
  branchCode: string | null;
  maskedAccountNumber: string | null;
  eligible: boolean;
  blockedReason: string | null;
};

export type DirectDebitBatch = {
  id: string;
  idempotencyKey: string;
  transferDate: string;
  description: string | null;
  status: "DRAFT" | "EXPORTED" | "COMPLETED" | "PARTIALLY_COMPLETED";
  requestedCount: number;
  totalAmount: number;
  succeededCount: number;
  failedCount: number;
  pendingCount: number;
  exportedAt: string | null;
  resultsImportedAt: string | null;
};

export type CssSelectedAccountsPreview = {
  fileHash: string;
  totalCount: number;
  newCount: number;
  changedCount: number;
  unchangedCount: number;
  items: Array<{
    subscriberCode: string;
    subscriberName: string;
    bankCode: string;
    branchCode: string;
    accountType: string;
    maskedAccountNumber: string;
    accountHolder: string;
    action: "NEW" | "CHANGED" | "UNCHANGED";
  }>;
};

export type TransferResultsPreview = {
  fileHash: string;
  totalCount: number;
  totalAmount: number;
  succeededCount: number;
  succeededAmount: number;
  failedCount: number;
  failedAmount: number;
  items: Array<{
    requestId: string;
    customerReference: string | null;
    amount: number;
    resultCode: string;
    resultMessage: string;
    succeeded: boolean;
  }>;
};

const downloadResponse = (data: BlobPart, contentType: unknown) =>
  new Blob([data], {
    type:
      typeof contentType === "string"
        ? contentType
        : "application/octet-stream",
  });

export const directDebitRepository = {
  getRegistrationStatusByParent: async (
    parentId: string
  ): Promise<MemberDirectDebitRegistration> => {
    const res = await httpDao.get<MemberDirectDebitRegistration>(
      `/admin/direct-debit/parents/${encodeURIComponent(parentId)}/registration-status`
    );
    return res.data;
  },
  getConfig: async (): Promise<DirectDebitConfig> => {
    const res = await httpDao.get<DirectDebitConfig>("/direct-debit/config");
    return res.data;
  },
  validateMockRegistrationSession: async (
    token: string
  ): Promise<MockRegistrationSession> => {
    const res = await httpDao.post<MockRegistrationSession>(
      "/direct-debit/mock-registration/validate",
      { token }
    );
    return res.data;
  },
  completeMockRegistrationSession: async (
    token: string,
    input: PublicMockBankAccountInput
  ): Promise<{ status: "REGISTERED" }> => {
    const res = await httpDao.post<{ status: "REGISTERED" }>(
      "/direct-debit/mock-registration/complete",
      { token, ...input }
    );
    return res.data;
  },
  getAdminConfig: async (): Promise<DirectDebitConfig> => {
    const res = await httpDao.get<DirectDebitConfig>(
      "/admin/direct-debit/config"
    );
    return res.data;
  },
  getRegistrationSettings:
    async (): Promise<DirectDebitRegistrationSettings> => {
      const res = await httpDao.get<DirectDebitRegistrationSettings>(
        "/admin/direct-debit/registration-settings"
      );
      return res.data;
    },
  updateRegistrationSettings: async (registrationUrl: string | null) => {
    const res = await httpDao.patch<DirectDebitRegistrationSettings>(
      "/admin/direct-debit/registration-settings",
      { registrationUrl: registrationUrl ?? "" }
    );
    return res.data;
  },
  getSchedules: async (year: number): Promise<DirectDebitScheduleList> => {
    const res = await httpDao.get<DirectDebitScheduleList>(
      "/admin/direct-debit/schedules",
      { params: { year } }
    );
    return res.data;
  },
  upsertSchedule: async (
    input: UpsertDirectDebitScheduleInput
  ): Promise<DirectDebitSchedule> => {
    const res = await httpDao.post<DirectDebitSchedule>(
      "/admin/direct-debit/schedules",
      input
    );
    return res.data;
  },
  syncCssProviderSchedules: async (): Promise<{
    sourceReference: string;
    createdCount: number;
    updatedCount: number;
    preservedInternalFinalizationCount: number;
    items: DirectDebitSchedule[];
  }> => {
    const res = await httpDao.post(
      "/admin/direct-debit/schedules/css-provider-sync"
    );
    return res.data;
  },
  updateInternalFinalizationDeadline: async (input: {
    scheduleId: string;
    internalFinalizationDeadlineAt: string;
  }): Promise<DirectDebitSchedule> => {
    const res = await httpDao.post<DirectDebitSchedule>(
      "/admin/direct-debit/schedules/internal-finalization",
      input
    );
    return res.data;
  },
  getCssEnrollmentCandidates: async (): Promise<{
    items: CssEnrollmentCandidate[];
  }> => {
    const res = await httpDao.get("/admin/direct-debit/css/enrollments");
    return res.data;
  },
  requestCssEnrollmentCorrection: async (
    enrollmentId: string,
    input: { reason: string; applicationRevision: number }
  ): Promise<CssCorrectionRequestResult> => {
    if (
      !/^[A-Za-z0-9][A-Za-z0-9-]{0,127}$/.test(enrollmentId) ||
      !Number.isInteger(input.applicationRevision) ||
      input.applicationRevision < 1 ||
      getCssCorrectionReasonError(input.reason)
    ) {
      throw new Error("修正依頼の内容が不正です");
    }
    const res = await httpDao.post<CssCorrectionRequestResult>(
      `/admin/direct-debit/css/enrollments/${encodeURIComponent(enrollmentId)}/correction-request`,
      { ...input, reason: normalizeCssCorrectionReason(input.reason) }
    );
    return res.data;
  },
  exportCssEnrollmentFile: async (parentIds: string[]): Promise<Blob> => {
    const res = await httpDao.post(
      "/admin/direct-debit/css/enrollments/request-file",
      { parentIds },
      { responseType: "blob" }
    );
    return downloadResponse(res.data, res.headers["content-type"]);
  },
  importCssSelectedAccounts: async (file: File) => {
    const form = new FormData();
    form.append("file", file);
    const res = await httpDao.post(
      "/admin/direct-debit/css/selected-accounts",
      form
    );
    return res.data as { importedCount: number };
  },
  previewCssSelectedAccounts: async (
    file: File
  ): Promise<CssSelectedAccountsPreview> => {
    const form = new FormData();
    form.append("file", file);
    const res = await httpDao.post(
      "/admin/direct-debit/css/selected-accounts/preview",
      form
    );
    return res.data;
  },
  getTransferCandidates: async (
    scheduleId: string
  ): Promise<{
    schedule: DirectDebitSchedule;
    items: DirectDebitCandidate[];
    eligibleCount: number;
    totalAmount: number;
  }> => {
    const res = await httpDao.get("/admin/direct-debit/transfer-candidates", {
      params: { scheduleId },
    });
    return res.data;
  },
  getTransferBatches: async (
    scheduleId: string
  ): Promise<{ items: DirectDebitBatch[] }> => {
    const res = await httpDao.get("/admin/direct-debit/transfer-batches", {
      params: { scheduleId },
    });
    return res.data;
  },
  createTransferBatch: async (input: {
    transferDate: string;
    description?: string;
    idempotencyKey: string;
    items: Array<{
      bankAccountId: string;
      tuitionInvoiceId: string;
      clientReference: string;
      amount: number;
    }>;
  }): Promise<DirectDebitBatch> => {
    const res = await httpDao.post(
      "/admin/direct-debit/transfer-batches",
      input
    );
    return res.data;
  },
  downloadTransferRequestFile: async (
    batchId: string
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await httpDao.get(
      `/admin/direct-debit/transfer-batches/${batchId}/request-file`,
      { responseType: "blob" }
    );
    const disposition = res.headers["content-disposition"];
    const filename =
      typeof disposition === "string"
        ? disposition.match(/filename="?([^";]+)"?/i)?.[1]
        : undefined;
    return {
      blob: downloadResponse(res.data, res.headers["content-type"]),
      filename: filename ?? "direct-debit-request.txt",
    };
  },
  previewTransferResults: async (
    batchId: string,
    file: File
  ): Promise<TransferResultsPreview> => {
    const form = new FormData();
    form.append("file", file);
    const res = await httpDao.post(
      `/admin/direct-debit/transfer-batches/${batchId}/results/preview`,
      form
    );
    return res.data;
  },
  importTransferResults: async (batchId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    const res = await httpDao.post(
      `/admin/direct-debit/transfer-batches/${batchId}/results`,
      form
    );
    return res.data as DirectDebitBatch;
  },
  registerMockBankAccount: async (
    input: Omit<RegisterMockBankAccountInput, "customerId">,
    signal?: AbortSignal
  ) => {
    const res = await httpDao.post(
      "/parent/direct-debit/mock-bank-account",
      input,
      { signal }
    );
    return res.data;
  },
};
